import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import Papa from 'papaparse';
import { Toaster, toast } from 'react-hot-toast';

// Import utility functions
import { parseFile } from './utils/fileParser';
import { formatContactsForExport } from './utils/contactUtils';
import { calculateDeliverabilityScore } from './utils/campaignUtils';

// Import components
import Sidebar from './components/Sidebar';

// Import pages
import DashboardPage from './pages/DashboardPage';
import UploadPage from './pages/UploadPage';
import ContactsPage from './pages/ContactsPage';
import TemplatesPage from './pages/TemplatesPage';
import CampaignsPage from './pages/CampaignsPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import { api } from './services/api';
import LoginPage from './pages/LoginPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import TeamPage from './pages/TeamPage';
import SmsRepliesPage from './pages/SmsRepliesPage';
import UnsubscribePage from './pages/UnsubscribePage';

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [authError, setAuthError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [unsubscribeQuery] = useState(() => {
    if (typeof window === 'undefined') return null;
    const path = (window.location.pathname || '').replace(/\/$/, '');
    if (path !== '/unsubscribe') return null;
    const params = new URLSearchParams(window.location.search || '');
    return {
      id: params.get('id') || '',
      email: params.get('email') || ''
    };
  });
  const [inviteToken, setInviteToken] = useState(() => {
    if (typeof window === 'undefined') return null;
    const path = window.location.pathname || '';
    const search = window.location.search || '';
    if (path.replace(/\/$/, '') === '/accept-invite') {
      const p = new URLSearchParams(search);
      return p.get('token') || null;
    }
    return null;
  });

  // Navigation state
  const [currentPage, setCurrentPage] = useState('dashboard');
  
  // Data state
  const [uploads, setUploads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [contactCounts, setContactCounts] = useState({ total: 0, email: 0, sms: 0, active: 0, suppressed: 0 });
  const [contactTagCounts, setContactTagCounts] = useState([]);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [contactsFilteredTotal, setContactsFilteredTotal] = useState(0);
  const [contactsPage, setContactsPage] = useState(1);
  const [contactsPageSize, setContactsPageSize] = useState(100);
  const [contactsTotalPages, setContactsTotalPages] = useState(1);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsInitialized, setContactsInitialized] = useState(false);
  const [selectingAllContacts, setSelectingAllContacts] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [suppressionList, setSuppressionList] = useState([]);
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  
  // UI state
  const [selectedTemplate, setSelectedTemplate] = useState(null);
const templateDefaults = useMemo(() => ({
  id: null,
  name: '',
  type: 'email',
  subject: '',
  previewText: '',
  body: '',
  category: 'Custom',
  fromName: '',
  fromEmail: '',
  replyTo: '',
  emailStyle: 'plain',
  accentColor: '#4f46e5',
  brandLabel: '',
  includeSignature: false,
  signature: '',
  customCss: ''
}), []);
  const [newTemplate, setNewTemplate] = useState(() => ({ ...templateDefaults }));
  const [dragActive, setDragActive] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [selectedContactsDetails, setSelectedContactsDetails] = useState(new Map());
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    channel: 'email',
    templateId: null,
    emailTemplateId: null,
    smsTemplateId: null,
    throttle: 500,
    sendAllAtOnce: true,
    scheduledDate: null,
    sendNow: true
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [contactFilter, setContactFilter] = useState('all');
  const [contactTagFilter, setContactTagFilter] = useState('');
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [activeTab, setActiveTab] = useState('list');
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateDeletingId, setTemplateDeletingId] = useState(null);
  const [campaignDeletingId, setCampaignDeletingId] = useState(null);
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [testSmsSending, setTestSmsSending] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [automationSaving, setAutomationSaving] = useState(false);
  const resetNewTemplate = useCallback(() => setNewTemplate({
    ...templateDefaults,
    fromName: settings?.from_name || '',
    fromEmail: settings?.from_email || '',
    replyTo: settings?.reply_to || ''
  }), [templateDefaults, settings]);

  const skipContactsFetchRef = useRef(false);
  const hasInjectedApiKeyRef = useRef(false);

  useEffect(() => {
    const initAuth = async () => {
      if (typeof window === 'undefined' || !window.localStorage) {
        setAuthChecked(true);
        return;
      }
      const token = window.localStorage.getItem('marketreachToken');
      const storedUser = window.localStorage.getItem('marketreachUser');
      if (token) {
        try {
          const res = await api.auth.me();
          if (res?.user) {
            setCurrentUser(res.user);
            setIsAuthenticated(true);
          } else {
            window.localStorage.removeItem('marketreachToken');
            window.localStorage.removeItem('marketreachUser');
            window.localStorage.removeItem('marketreachAuth');
          }
        } catch (_) {
          window.localStorage.removeItem('marketreachToken');
          window.localStorage.removeItem('marketreachUser');
          window.localStorage.removeItem('marketreachAuth');
        }
      } else if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          if (user?.email) setCurrentUser(user);
        } catch (_) {}
      }
      setAuthChecked(true);
    };
    initAuth();
  }, []);

  const handleLogin = async (email, password) => {
    setAuthError('');
    try {
      const res = await api.auth.login(email.trim(), password);
      if (res?.token && res?.user) {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.setItem('marketreachToken', res.token);
          window.localStorage.setItem('marketreachUser', JSON.stringify(res.user));
          window.localStorage.setItem('marketreachAuth', 'true');
        }
        setCurrentUser(res.user);
        setIsAuthenticated(true);
        setCurrentPage('dashboard');
        setContactsInitialized(false);
        setIsLoading(true);
        skipContactsFetchRef.current = false;
        toast.success('Welcome back!');
      }
    } catch (err) {
      setAuthError(err?.payload?.error || err?.message || 'Invalid email or password.');
    }
  };

  const handleLogout = useCallback(() => {
    setIsAuthenticated(false);
    setCurrentPage('dashboard');
    setUploads([]);
    setContacts([]);
    setContactCounts({ total: 0, email: 0, sms: 0, active: 0, suppressed: 0 });
    setContactTagCounts([]);
    setContactsTotal(0);
    setContactsFilteredTotal(0);
    setContactsPage(1);
    setContactsPageSize(100);
    setContactsTotalPages(1);
    setContactsInitialized(false);
    setContactsLoading(false);
    setSelectingAllContacts(false);
    setTemplates([]);
    setCampaigns([]);
    setSuppressionList([]);
    setSettings(null);
    setIsLoading(false);
    setLoadError(null);
    setSelectedTemplate(null);
    setNewTemplate({ ...templateDefaults });
    setDragActive(false);
    setSelectedContacts(new Set());
    setSelectedContactsDetails(new Map());
    setCampaignForm({
      name: '',
      channel: 'email',
      templateId: null,
      emailTemplateId: null,
      smsTemplateId: null,
      throttle: 500,
      sendAllAtOnce: true,
      scheduledDate: null,
      sendNow: true
    });
    setSearchTerm('');
    setContactFilter('all');
    setContactTagFilter('');
    setExpandedCampaign(null);
    setActiveTab('list');
    setTemplateSaving(false);
    setTemplateDeletingId(null);
    setCampaignDeletingId(null);
    setAuthError('');
    setCurrentUser(null);
    skipContactsFetchRef.current = false;
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem('marketreachAuth');
      window.localStorage.removeItem('marketreachToken');
      window.localStorage.removeItem('marketreachUser');
    }
    toast('Signed out');
  }, [templateDefaults]);

  useEffect(() => {
    const clientKey = process.env.REACT_APP_API_KEY;
    if (clientKey && !hasInjectedApiKeyRef.current && typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('marketreachApiKey', clientKey);
      hasInjectedApiKeyRef.current = true;
    }
  }, []);

  const loadInitialData = useCallback(async () => {
    try {
      setIsLoading(true);
      setLoadError(null);
      const [
        contactsResponse,
        templatesData,
        campaignsData,
        suppressionData,
        settingsData
      ] = await Promise.all([
        api.contacts.list({ page: 1, limit: contactsPageSize, filter: 'all' }),
        api.templates.list(),
        api.campaigns.list(),
        api.suppression.list(),
        api.settings.get()
      ]);
      const limitUsed = contactsResponse.page?.limit || contactsPageSize;
      const totalPages = Math.max(
        1,
        contactsResponse.page?.totalPages || Math.ceil((contactsResponse.filteredTotal || 0) / limitUsed) || 1
      );
      setContacts(contactsResponse.contacts || []);
      setContactCounts(contactsResponse.counts || { total: 0, email: 0, sms: 0, active: 0, suppressed: 0 });
      setContactTagCounts(contactsResponse.tagCounts || []);
      setContactsTotal(contactsResponse.total || 0);
      setContactsFilteredTotal(contactsResponse.filteredTotal || 0);
      setContactsPage(contactsResponse.page?.current || 1);
      setContactsPageSize(limitUsed);
      setContactsTotalPages(totalPages);
      setTemplates(templatesData);
      setCampaigns(campaignsData);
      setSuppressionList(suppressionData);
      setSettings(settingsData);
      skipContactsFetchRef.current = true;
      setContactsInitialized(true);
    } catch (error) {
      console.error('Failed to load initial data', error);
      setLoadError(error);
    } finally {
      setIsLoading(false);
    }
  }, [contactsPageSize]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    loadInitialData();
  }, [loadInitialData, isAuthenticated]);

  const fetchContacts = useCallback(async (overrides = {}) => {
    const params = {
      page: overrides.page ?? contactsPage,
      limit: overrides.limit ?? contactsPageSize,
      filter: overrides.filter ?? contactFilter,
      search: overrides.search ?? searchTerm,
      tag: overrides.tag ?? contactTagFilter
    };

    setContactsLoading(true);
    try {
      const response = await api.contacts.list(params);
      const limitUsed = response.page?.limit || params.limit;
      const totalPages = Math.max(
        1,
        response.page?.totalPages || Math.ceil((response.filteredTotal || 0) / limitUsed) || 1
      );

      if (params.page > totalPages) {
        return fetchContacts({
          page: totalPages,
          limit: params.limit,
          filter: params.filter,
          search: params.search
        });
      }

      setContacts(response.contacts || []);
      setContactCounts(response.counts || { total: 0, email: 0, sms: 0, active: 0, suppressed: 0 });
      setContactTagCounts(response.tagCounts || []);
      setContactsTotal(response.total || 0);
      setContactsFilteredTotal(response.filteredTotal || 0);
      setContactsPage(response.page?.current || params.page);
      setContactsPageSize(limitUsed);
      setContactsTotalPages(totalPages);
      setSelectedContactsDetails((prev) => {
        if (!prev.size) return prev;
        const next = new Map(prev);
        (response.contacts || []).forEach((contact) => {
          if (next.has(contact.id)) {
            next.set(contact.id, contact);
          }
        });
        return next;
      });
    } catch (error) {
      console.error('Failed to load contacts', error);
      toast.error('Failed to load contacts. Please try again.');
    } finally {
      setContactsLoading(false);
    }
  }, [contactsPage, contactsPageSize, contactFilter, searchTerm, contactTagFilter]);

  useEffect(() => {
    if (!contactsInitialized || !isAuthenticated) return undefined;
    if (skipContactsFetchRef.current) {
      skipContactsFetchRef.current = false;
      return undefined;
    }
    const handle = setTimeout(() => {
      fetchContacts();
    }, 300);
    return () => clearTimeout(handle);
  }, [contactsInitialized, fetchContacts, isAuthenticated]);

  const handleSearchTermChange = useCallback((value) => {
    setContactsPage(1);
    setSearchTerm(value);
  }, []);

  const handleContactFilterChange = useCallback((value) => {
    setContactsPage(1);
    setContactFilter(value);
  }, []);

  const handleTagFilterChange = useCallback((value) => {
    skipContactsFetchRef.current = true;
    setContactsPage(1);
    setContactTagFilter(value);
    fetchContacts({ page: 1, tag: value });
  }, [fetchContacts]);

  const handleContactsPageChange = useCallback((page) => {
    skipContactsFetchRef.current = true;
    setContactsPage(page);
    fetchContacts({ page });
  }, [fetchContacts]);

  const handleContactsPageSizeChange = useCallback((size) => {
    skipContactsFetchRef.current = true;
    setContactsPageSize(size);
    setContactsPage(1);
    fetchContacts({ page: 1, limit: size });
  }, [fetchContacts]);

  const toggleContactSelection = useCallback((contact, isSelected) => {
    setSelectedContacts((prev) => {
      const next = new Set(prev);
      if (isSelected) {
        next.add(contact.id);
      } else {
        next.delete(contact.id);
      }
      return next;
    });
    setSelectedContactsDetails((prev) => {
      const next = new Map(prev);
      if (isSelected) {
        next.set(contact.id, contact);
      } else {
        next.delete(contact.id);
      }
      return next;
    });
  }, []);

  const toggleSelectAllContacts = useCallback((contactsOnPage, isSelected) => {
    if (!Array.isArray(contactsOnPage) || !contactsOnPage.length) return;
    setSelectedContacts((prev) => {
      const next = new Set(prev);
      contactsOnPage.forEach((contact) => {
        if (isSelected) {
          next.add(contact.id);
        } else {
          next.delete(contact.id);
        }
      });
      return next;
    });
    setSelectedContactsDetails((prev) => {
      const next = new Map(prev);
      contactsOnPage.forEach((contact) => {
        if (isSelected) {
          next.set(contact.id, contact);
        } else {
          next.delete(contact.id);
        }
      });
      return next;
    });
  }, []);

  const selectAllFilteredContacts = useCallback(async () => {
    if (selectingAllContacts) return;
    setSelectingAllContacts(true);
    try {
      const limit = 1000;
      let page = 1;
      let totalPages = 1;
      const aggregateIds = new Set();
      const aggregateDetails = new Map();
      do {
        const response = await api.contacts.list({
          page,
          limit,
          filter: contactFilter,
          search: searchTerm || undefined,
          tag: contactTagFilter || undefined
        });
        (response.contacts || []).forEach((contact) => {
          aggregateIds.add(contact.id);
          aggregateDetails.set(contact.id, contact);
        });
        totalPages = response.page?.totalPages || 1;
        page += 1;
      } while (page <= totalPages);

      setSelectedContacts(aggregateIds);
      setSelectedContactsDetails(aggregateDetails);
      toast.success(`${aggregateIds.size.toLocaleString()} contacts selected`);
    } catch (error) {
      console.error('Failed to select all contacts', error);
      toast.error('Unable to select all contacts. Please try again.');
    } finally {
      setSelectingAllContacts(false);
    }
  }, [contactFilter, contactTagFilter, searchTerm, selectingAllContacts]);

  const clearAllSelectedContacts = useCallback(() => {
    setSelectedContacts(new Set());
    setSelectedContactsDetails(new Map());
    toast('Selection cleared');
  }, []);

  const hasActiveCampaigns = useMemo(
    () => campaigns.some((campaign) => ['queued', 'scheduled', 'in_progress'].includes(campaign.status)),
    [campaigns]
  );

  useEffect(() => {
    if (!hasActiveCampaigns || !isAuthenticated) return undefined;
    const interval = setInterval(async () => {
      try {
        const campaignData = await api.campaigns.list();
        setCampaigns(campaignData);
      } catch (error) {
        console.error('Failed to refresh campaign progress', error);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [hasActiveCampaigns, isAuthenticated]);

  const processFiles = useCallback(async (files) => {
    const CHUNK_SIZE = 5000;
    for (const file of files) {
      const sourceDisplay = file.name || 'upload';
      const sourceKey = sourceDisplay.trim().toLowerCase();
      const upload = {
        id: Date.now() + Math.random(),
        filename: sourceDisplay,
        sourceKey,
        status: 'processing',
        date: new Date().toISOString(),
        size: file.size
      };
      setUploads(prev => [...prev, upload]);

      try {
        const extracted = await parseFile(file, { sourceKey, sourceDisplay });

        if (!extracted.length) {
          setUploads(prev => prev.map(u =>
            u.id === upload.id ? {
              ...u,
              status: 'failed',
              error: 'No contacts found. Include an Email or Phone Number column.',
              count: 0,
              newCount: 0,
              updated: 0,
              duplicates: 0
            } : u
          ));
          toast.error(`No contacts found in ${file.name}`);
          continue;
        }

        let createdTotal = 0;
        let updatedTotal = 0;
        const totalProcessed = extracted.length;

        for (let index = 0; index < extracted.length; index += CHUNK_SIZE) {
          const chunk = extracted.slice(index, index + CHUNK_SIZE);
          const summary = await api.contacts.bulkImport(chunk);
          createdTotal += summary.created;
          updatedTotal += summary.updated;
        }

        skipContactsFetchRef.current = true;
        setContactsPage(1);
        await fetchContacts({ page: 1 });

        const duplicateCount = Math.max(0, totalProcessed - createdTotal - updatedTotal);
        setUploads(prev => prev.map(u =>
          u.id === upload.id ? {
            ...u,
            status: 'completed',
            count: totalProcessed,
            newCount: createdTotal,
            updated: updatedTotal,
            duplicates: duplicateCount
          } : u
        ));
        toast.success(`${createdTotal.toLocaleString()} new contacts imported from ${file.name}`);
      } catch (error) {
        console.error('Failed to process file', error);
        setUploads(prev => prev.map(u =>
          u.id === upload.id ? { ...u, status: 'failed', error: error.message } : u
        ));
        toast.error(`Failed to import ${file.name}`);
      }
    }
  }, [fetchContacts]);

  // File upload handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = [...e.dataTransfer.files];
    await processFiles(files);
  }, [processFiles]);

  const handleFileInput = useCallback(async (e) => {
    const files = [...e.target.files];
    await processFiles(files);
  }, [processFiles]);

  // Campaign creation
  const createCampaign = useCallback(async () => {
    const template = templates.find(t => t.id === campaignForm.templateId);
    const emailTemplate = templates.find(t => t.id === campaignForm.emailTemplateId);
    const smsTemplate = templates.find(t => t.id === campaignForm.smsTemplateId);
    if (campaignForm.channel === 'multi' && (!emailTemplate || !smsTemplate)) {
      toast.error('Select both an email template and an SMS template before creating a multichannel campaign');
      return;
    }
    if (campaignForm.channel !== 'multi' && !template) {
      toast.error('Select a template before creating a campaign');
      return;
    }

    const recipientContacts = Array.from(selectedContacts)
      .map((id) => selectedContactsDetails.get(id))
      .filter(Boolean);
    
    if (!recipientContacts.length) {
      toast.error('No valid recipients selected (check suppression list)');
      return;
    }

    try {
      const createdCampaign = await api.campaigns.create({
        name: campaignForm.name,
        channel: campaignForm.channel,
        templateId: template?.id || null,
        emailTemplateId: emailTemplate?.id || null,
        smsTemplateId: smsTemplate?.id || null,
        recipientIds: Array.from(selectedContacts),
        throttle: campaignForm.sendAllAtOnce ? 999999 : campaignForm.throttle,
        sendNow: campaignForm.sendNow,
        scheduledDate: campaignForm.scheduledDate
      });
      if (createdCampaign?.mode === 'multi' && Array.isArray(createdCampaign.campaigns)) {
        setCampaigns(prev => [...createdCampaign.campaigns, ...prev]);
      } else {
        setCampaigns(prev => [createdCampaign, ...prev]);
      }
      setSelectedContacts(new Set());
      setCampaignForm({
        name: '',
        channel: 'email',
        templateId: null,
        emailTemplateId: null,
        smsTemplateId: null,
        throttle: 500,
        sendAllAtOnce: true,
        scheduledDate: null,
        sendNow: true
      });
      setCurrentPage('campaigns');
      toast.success(createdCampaign?.mode === 'multi' ? 'Email and SMS campaigns queued' : 'Campaign queued for sending');
    } catch (error) {
      console.error('Failed to create campaign', error);
      toast.error(error?.payload?.error || error?.message || 'Failed to create campaign. Please try again.');
    }
  }, [campaignForm, selectedContacts, selectedContactsDetails, templates, setCurrentPage]);

  // Template management
  const saveTemplate = useCallback(async () => {
    if (!newTemplate.name || !newTemplate.body || (newTemplate.type === 'email' && !newTemplate.subject)) {
      return;
    }

    setTemplateSaving(true);
    try {
      const templatePayload = {
        name: newTemplate.name,
        type: newTemplate.type,
        category: newTemplate.category,
        body: newTemplate.body,
        subject: newTemplate.type === 'email' ? newTemplate.subject : undefined,
        previewText: newTemplate.type === 'email' ? newTemplate.previewText : undefined,
        fromName: newTemplate.type === 'email' ? newTemplate.fromName : undefined,
        fromEmail: newTemplate.type === 'email' ? newTemplate.fromEmail : undefined,
        replyTo: newTemplate.type === 'email' ? newTemplate.replyTo : undefined,
        emailStyle: newTemplate.type === 'email' ? newTemplate.emailStyle : undefined,
        accentColor: newTemplate.type === 'email' ? newTemplate.accentColor : undefined,
        brandLabel: newTemplate.type === 'email' ? newTemplate.brandLabel : undefined,
        includeSignature: newTemplate.type === 'email' ? Boolean(newTemplate.includeSignature) : false,
        signature: newTemplate.type === 'email' ? newTemplate.signature : undefined,
        customCss: newTemplate.type === 'email' ? newTemplate.customCss : undefined
      };

      if (newTemplate.id) {
        const updatedTemplate = await api.templates.update(newTemplate.id, templatePayload);
        setTemplates(prev => prev.map(t => (t.id === updatedTemplate.id ? updatedTemplate : t)));
        setSelectedTemplate(updatedTemplate);
      } else {
        const createdTemplate = await api.templates.create(templatePayload);
        setTemplates(prev => [createdTemplate, ...prev]);
        setSelectedTemplate(createdTemplate);
      }

      resetNewTemplate();
      setActiveTab('list');
      toast.success(newTemplate.id ? 'Template updated' : 'Template created');
    } catch (error) {
      console.error('Failed to save template', error);
      toast.error('Failed to save template. Please try again.');
    } finally {
      setTemplateSaving(false);
    }
  }, [newTemplate, resetNewTemplate]);

  // Contact management
  const addToSuppression = useCallback(async (contact, reason) => {
    try {
      const entry = await api.suppression.create({
        email: contact.email,
        phone: contact.phone,
        reason
      });
      setSuppressionList(prev => [entry, ...prev]);
      setContacts(prev => prev.map(c => 
        c.id === contact.id ? { ...c, status: 'suppressed' } : c
      ));
      if (contact.id) {
        await api.contacts.update(contact.id, { status: 'suppressed' });
      }
      setSelectedContactsDetails((prev) => {
        if (!prev.has(contact.id)) return prev;
        const next = new Map(prev);
        const existing = next.get(contact.id) || {};
        next.set(contact.id, { ...existing, status: 'suppressed' });
        return next;
      });
      skipContactsFetchRef.current = true;
      await fetchContacts();
      toast.success('Contact suppressed');
    } catch (error) {
      console.error('Failed to add to suppression list', error);
      toast.error('Failed to add contact to suppression list. Please try again.');
    }
  }, [fetchContacts]);

  const deleteTemplate = useCallback(async (templateId) => {
    try {
      setTemplateDeletingId(templateId);
      await api.templates.remove(templateId);
      setTemplates(prev => prev.filter(t => t.id !== templateId));
      if (selectedTemplate?.id === templateId) {
        setSelectedTemplate(null);
      }
      if (newTemplate.id === templateId) {
        resetNewTemplate();
      }
      toast.success('Template deleted');
    } catch (error) {
      console.error('Failed to delete template', error);
      toast.error('Failed to delete template. Please try again.');
    } finally {
      setTemplateDeletingId(null);
    }
  }, [newTemplate.id, resetNewTemplate, selectedTemplate]);

  const loadTemplateForEdit = useCallback((template) => {
    setActiveTab('create');
    setNewTemplate({
      id: template.id,
      name: template.name,
      type: template.type,
      subject: template.subject || '',
      previewText: template.previewText || '',
      body: template.body,
      category: template.category || 'Custom',
      fromName: template.fromName || settings?.from_name || '',
      fromEmail: template.fromEmail || settings?.from_email || '',
      replyTo: template.replyTo || settings?.reply_to || '',
      emailStyle: template.emailStyle || 'plain',
      accentColor: template.accentColor || '#4f46e5',
      brandLabel: template.brandLabel || '',
      includeSignature: Boolean(template.includeSignature),
      signature: template.signature || '',
      customCss: template.customCss || ''
    });
  }, [settings]);

  const deleteContact = useCallback(async (contactId) => {
    try {
      await api.contacts.remove(contactId);
      setContacts(prev => prev.filter(c => c.id !== contactId));
      setSelectedContacts(prev => {
        const updated = new Set(prev);
        updated.delete(contactId);
        return updated;
      });
      setSelectedContactsDetails(prev => {
        if (!prev.has(contactId)) return prev;
        const next = new Map(prev);
        next.delete(contactId);
        return next;
      });
      skipContactsFetchRef.current = true;
      await fetchContacts();
      toast.success('Contact deleted');
    } catch (error) {
      console.error('Failed to delete contact', error);
      toast.error('Failed to delete contact. Please try again.');
    }
  }, [fetchContacts]);

  const deleteUploadRecord = useCallback(async (upload, confirmation) => {
    if (!upload) return;
    if (upload.status === 'processing') {
      toast.error('Upload is still processing. Please wait for it to finish.');
      return;
    }

    if (confirmation !== 'DELETE') {
      toast.error('Type DELETE to confirm.');
      return false;
    }

    try {
      await api.contacts.removeBySource(upload.sourceKey || upload.filename);
      setUploads((prev) => prev.filter((item) => item.id !== upload.id));
      skipContactsFetchRef.current = true;
      await fetchContacts({ page: 1 });
      toast.success(`Removed contacts imported from ${upload.filename}`);
      return true;
    } catch (error) {
      console.error('Failed to delete upload contacts', error);
      toast.error('Failed to delete upload. Please try again.');
      return false;
    }
  }, [fetchContacts]);

  const deleteAllContacts = useCallback(async (confirmation) => {
    if (confirmation !== 'DELETE') {
      toast.error('Type DELETE to confirm bulk removal.');
      return false;
    }

    try {
      const { deleted } = await api.contacts.removeAll(confirmation);
      setContacts([]);
      setContactCounts({ total: 0, email: 0, sms: 0, active: 0, suppressed: 0 });
      setContactTagCounts([]);
      setContactsTotal(0);
      setContactsFilteredTotal(0);
      setContactsTotalPages(1);
      setContactsPage(1);
      setContactTagFilter('');
      setSelectedContacts(new Set());
      setSelectedContactsDetails(new Map());
      skipContactsFetchRef.current = true;
      toast.success(`${deleted.toLocaleString()} contacts deleted`);
      return true;
    } catch (error) {
      console.error('Failed to delete all contacts', error);
      toast.error('Failed to delete contacts. Please try again.');
      return false;
    }
  }, []);

  const sendTestEmail = useCallback(async () => {
    if (!campaignForm.templateId) {
      toast.error('Please select a template first');
      return;
    }

    try {
      setTestEmailSending(true);
      const result = await api.campaigns.sendTestEmail(campaignForm.templateId);
      if (result.success) {
        toast.success(`Test email sent to ${result.sent} recipient(s)`);
      } else {
        toast.error('Failed to send test email');
      }
    } catch (error) {
      console.error('Failed to send test email', error);
      toast.error('Failed to send test email. Please try again.');
    } finally {
      setTestEmailSending(false);
    }
  }, [campaignForm.templateId]);

  const sendSingleEmail = useCallback(async (templateId, contactId, email) => {
    if (!templateId) {
      toast.error('Template is required');
      return;
    }

    if (!contactId && !email) {
      toast.error('Either contact or email address is required');
      return;
    }

    try {
      const result = await api.campaigns.sendSingleEmail(templateId, contactId, email);
      if (result.success) {
        toast.success(`Email sent successfully to ${result.recipient}`);
      } else {
        toast.error('Failed to send email');
      }
    } catch (error) {
      console.error('Failed to send single email', error);
      toast.error(error.message || 'Failed to send email. Please try again.');
    }
  }, []);

  const sendTestSms = useCallback(async (templateId, contactId, phone) => {
    if (!templateId) {
      toast.error('Please select an SMS template first');
      return;
    }

    if (!contactId && !phone) {
      toast.error('Please choose a contact or enter a phone number');
      return;
    }

    try {
      setTestSmsSending(true);
      const result = await api.campaigns.testSms(templateId, contactId, phone);
      if (result.success) {
        toast.success(`Test SMS sent to ${result.recipient}`);
      } else {
        toast.error('Failed to send test SMS');
      }
    } catch (error) {
      console.error('Failed to send test SMS', error);
      toast.error(error.message || 'Failed to send test SMS. Please try again.');
    } finally {
      setTestSmsSending(false);
    }
  }, []);

  const sendSingleSms = useCallback(async (templateId, contactId, phone) => {
    if (!templateId) {
      toast.error('Template is required');
      return;
    }

    if (!contactId && !phone) {
      toast.error('Either contact or phone number is required');
      return;
    }

    try {
      const result = await api.campaigns.sendSingleSms(templateId, contactId, phone);
      if (result.success) {
        toast.success(`SMS sent successfully to ${result.recipient}`);
      } else {
        toast.error('Failed to send SMS');
      }
    } catch (error) {
      console.error('Failed to send single SMS', error);
      toast.error(error.message || 'Failed to send SMS. Please try again.');
    }
  }, []);

  const saveSettings = useCallback(async () => {
    if (!settings) {
      toast.error('Settings are not loaded yet.');
      return;
    }

    try {
      setSettingsSaving(true);
      const updated = await api.settings.update(settings);
      const { twilioMessagingWebhookSync, ...rest } = updated;
      setSettings((prev) => ({
        ...rest,
        sendgrid_api_key: prev?.sendgrid_api_key || '',
        twilio_account_sid: prev?.twilio_account_sid || '',
        twilio_auth_token: prev?.twilio_auth_token || '',
        twilio_messaging_service_sid: prev?.twilio_messaging_service_sid || rest.twilio_messaging_service_sid || '',
        twilio_from_number: prev?.twilio_from_number || rest.twilio_from_number || ''
      }));
      if (twilioMessagingWebhookSync?.synced) {
        toast.success('Settings saved. Twilio Messaging Service webhooks updated for inbound + delivery.');
      } else if (twilioMessagingWebhookSync && !twilioMessagingWebhookSync.skipped && twilioMessagingWebhookSync.synced === false) {
        toast.success('Settings saved');
        toast.error(
          `Twilio Messaging Service webhook sync failed: ${twilioMessagingWebhookSync.error || 'Unknown error'}`
        );
      } else {
        toast.success('Settings saved');
      }
    } catch (error) {
      console.error('Failed to save settings', error);
      toast.error(error.message || 'Failed to save settings. Please try again.');
    } finally {
      setSettingsSaving(false);
    }
  }, [settings]);

  const setAutomation = useCallback(async (mode, confirm = false) => {
    try {
      setAutomationSaving(true);
      const updated = await api.settings.setAutomation(mode, confirm);
      setSettings((prev) => ({
        ...updated,
        sendgrid_api_key: prev?.sendgrid_api_key || '',
        twilio_account_sid: prev?.twilio_account_sid || '',
        twilio_auth_token: prev?.twilio_auth_token || '',
        twilio_messaging_service_sid: prev?.twilio_messaging_service_sid || updated.twilio_messaging_service_sid || '',
        twilio_from_number: prev?.twilio_from_number || updated.twilio_from_number || ''
      }));
      toast.success(mode === 'on'
        ? 'Automation is on. The first email can send.'
        : mode === 'test'
          ? 'Test mode is on. Only johncarterson101@gmail.com can receive TRADEIT DAY email.'
          : 'TRADEIT DAY email is off.');
    } catch (error) {
      toast.error(error.message || 'Could not change automation.');
      throw error;
    } finally {
      setAutomationSaving(false);
    }
  }, []);

  const deleteCampaign = useCallback(async (campaignId) => {
    try {
      setCampaignDeletingId(campaignId);
      await api.campaigns.remove(campaignId);
      setCampaigns(prev => prev.filter(c => c.id !== campaignId));
      toast.success('Campaign deleted');
    } catch (error) {
      console.error('Failed to delete campaign', error);
      toast.error('Failed to delete campaign. Please try again.');
    } finally {
      setCampaignDeletingId(null);
    }
  }, []);

  // Export functions
  const exportContacts = useCallback(async () => {
    try {
      const pageSize = 1000;
      let page = 1;
      let allContacts = [];
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const response = await api.contacts.list({
          page,
          limit: pageSize,
          filter: contactFilter,
          search: searchTerm || undefined
        });
        allContacts = allContacts.concat(response.contacts || []);
        const limitUsed = response.page?.limit || pageSize;
        const totalPages = Math.max(
          1,
          response.page?.totalPages || Math.ceil((response.filteredTotal || 0) / limitUsed) || 1
        );
        if (page >= totalPages) {
          break;
        }
        page += 1;
      }

      const formattedContacts = formatContactsForExport(allContacts);
      const csv = Papa.unparse(formattedContacts);
      
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contacts-${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export contacts', error);
      toast.error('Failed to export contacts. Please try again.');
    }
  }, [contactFilter, searchTerm]);

  const exportCampaignReport = (campaign) => {
    const csv = Papa.unparse([{
      'Campaign Name': campaign.name,
      'Channel': campaign.channel,
      'Template': campaign.template,
      'Sent': campaign.sent,
      'Delivered': campaign.delivered,
      'Delivery Rate': ((campaign.delivered / campaign.sent) * 100).toFixed(2) + '%',
      'Opened': campaign.opened,
      'Open Rate': campaign.channel === 'email' 
        ? ((campaign.opened / campaign.delivered) * 100).toFixed(2) + '%' 
        : 'N/A',
      'Clicked': campaign.clicked,
      'Click Rate': campaign.channel === 'email' && campaign.opened > 0
        ? ((campaign.clicked / campaign.opened) * 100).toFixed(2) + '%' 
        : 'N/A',
      'Replied': campaign.replied,
      'Reply Rate': campaign.channel === 'sms' 
        ? ((campaign.replied / campaign.delivered) * 100).toFixed(2) + '%' 
        : 'N/A',
      'Bounced': campaign.bounced,
      'Unsubscribed': campaign.unsubscribed,
      'Complained': campaign.complained,
      'Status': campaign.status,
      'Created': new Date(campaign.createdAt).toLocaleString(),
      'Sent Date': campaign.sentDate ? new Date(campaign.sentDate).toLocaleString() : 'Scheduled'
    }]);
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `campaign-${campaign.name}-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Calculate deliverability score
  const deliverabilityScore = useMemo(
    () => (settings ? calculateDeliverabilityScore(settings, campaigns) : null), 
    [settings, campaigns]
  );

  // Page routing
  const pages = {
    dashboard: (
      <DashboardPage 
        contactCounts={contactCounts}
        campaigns={campaigns}
        templates={templates}
        deliverabilityScore={deliverabilityScore}
        setCurrentPage={setCurrentPage}
        automationMode={settings?.tradeitDayAutomation || 'off'}
      />
    ),
    upload: (
      <UploadPage
        dragActive={dragActive}
        handleDrag={handleDrag}
        handleDrop={handleDrop}
        handleFileInput={handleFileInput}
        uploads={uploads}
        contacts={contacts}
        contactsTotal={contactsTotal}
        deleteUploadRecord={deleteUploadRecord}
        setCurrentPage={setCurrentPage}
      />
    ),
    contacts: (
      <ContactsPage
        contacts={contacts}
        searchTerm={searchTerm}
        onSearchTermChange={handleSearchTermChange}
        contactFilter={contactFilter}
        onContactFilterChange={handleContactFilterChange}
        contactCounts={contactCounts}
        contactTagCounts={contactTagCounts}
        contactTagFilter={contactTagFilter}
        onTagFilterChange={handleTagFilterChange}
        contactsTotal={contactsTotal}
        contactsFilteredTotal={contactsFilteredTotal}
        contactsPage={contactsPage}
        contactsPageSize={contactsPageSize}
        contactsTotalPages={contactsTotalPages}
        onPageChange={handleContactsPageChange}
        onPageSizeChange={handleContactsPageSizeChange}
        contactsLoading={contactsLoading}
        selectingAllContacts={selectingAllContacts}
        onSelectAllFiltered={selectAllFilteredContacts}
        onClearSelection={clearAllSelectedContacts}
        selectedContacts={selectedContacts}
        onToggleContact={toggleContactSelection}
        onToggleSelectAll={toggleSelectAllContacts}
        exportContacts={exportContacts}
        addToSuppression={addToSuppression}
        deleteContact={deleteContact}
        deleteAllContacts={deleteAllContacts}
        setCurrentPage={setCurrentPage}
      />
    ),
    templates: (
      <TemplatesPage
        templates={templates}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        newTemplate={newTemplate}
        setNewTemplate={setNewTemplate}
        saveTemplate={saveTemplate}
        templateSaving={templateSaving}
        templateDeletingId={templateDeletingId}
        selectedTemplate={selectedTemplate}
        setSelectedTemplate={setSelectedTemplate}
        deleteTemplate={deleteTemplate}
        loadTemplateForEdit={loadTemplateForEdit}
        resetNewTemplate={resetNewTemplate}
        settings={settings}
      />
    ),
    'sms-replies': <SmsRepliesPage />,
    campaigns: (
      <CampaignsPage
        campaigns={campaigns}
        templates={templates}
        contacts={contacts}
        selectedContacts={selectedContacts}
        selectedContactsDetails={selectedContactsDetails}
        settings={settings}
        campaignForm={campaignForm}
        setCampaignForm={setCampaignForm}
        createCampaign={createCampaign}
        sendTestEmail={sendTestEmail}
        sendTestSms={sendTestSms}
        sendSingleEmail={sendSingleEmail}
        sendSingleSms={sendSingleSms}
        testEmailSending={testEmailSending}
        testSmsSending={testSmsSending}
        expandedCampaign={expandedCampaign}
        setExpandedCampaign={setExpandedCampaign}
        exportCampaignReport={exportCampaignReport}
        setCurrentPage={setCurrentPage}
        deleteCampaign={deleteCampaign}
        campaignDeletingId={campaignDeletingId}
      />
    ),
    team: (
      <TeamPage />
    ),
    settings: (
      <SettingsPage
        settings={settings}
        setSettings={setSettings}
        suppressionList={suppressionList}
        setSuppressionList={setSuppressionList}
        setContacts={setContacts}
        onSave={saveSettings}
        settingsSaving={settingsSaving}
        onSetAutomation={setAutomation}
        automationSaving={automationSaving}
      />
    ),
    reports: (
      <ReportsPage
        campaigns={campaigns}
        deliverabilityScore={deliverabilityScore}
      />
    ),
  };

  if (unsubscribeQuery) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50 to-purple-50">
        <UnsubscribePage id={unsubscribeQuery.id} email={unsubscribeQuery.email} />
        <Toaster position="top-right" />
      </div>
    );
  }

  if (!authChecked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 text-gray-600">
        Initializing...
      </div>
    );
  }

  if (inviteToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50 to-purple-50">
        <AcceptInvitePage
          token={inviteToken}
          onAccept={(user) => {
            setCurrentUser(user);
            setIsAuthenticated(true);
            setInviteToken(null);
            setCurrentPage('dashboard');
            setContactsInitialized(false);
            setIsLoading(true);
            skipContactsFetchRef.current = false;
            toast.success('Account created. Welcome!');
          }}
        />
        <Toaster position="top-right" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50 to-purple-50">
        <LoginPage onLogin={handleLogin} error={authError} />
        <Toaster position="top-right" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50 to-purple-50">
      <Sidebar 
        currentPage={currentPage} 
        setCurrentPage={setCurrentPage}
        contactsTotal={contactsTotal}
        templates={templates}
        campaigns={campaigns}
        deliverabilityScore={deliverabilityScore}
        onLogout={handleLogout}
        userEmail={currentUser?.email}
      />
      <div className="ml-64 p-8">
        {loadError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            Failed to load data from the server. Some information may be outdated until the connection is restored.
          </div>
        )}
        {isLoading ? (
          <div className="flex h-full items-center justify-center text-gray-600">
            Loading dashboard data...
          </div>
        ) : (
          pages[currentPage]
        )}
      </div>
      <Toaster position="top-right" />
    </div>
  );
};

export default App;
